import React, { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore"; // Firestore imports
import { db } from "../config/Firebase"; // Import your Firebase configuration
import { useNavigate } from "react-router-dom"; // Import useNavigate
import Header from "./Header";
import Footer from "./Footer";
// Carousel Component: Rotates images every 3 seconds
function Carousel({ images }) {
  // Filter out any undefined or null images
  const validImages = images.filter((image) => image);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % validImages.length);
    }, 3000); // Change image every 3 seconds

    return () => clearInterval(interval);
  }, [validImages]);

  if (validImages.length === 0) {
    return <p className="text-gray-400">No images available</p>;
  }

  return (
    <div className="w-full h-64 relative overflow-hidden">
      {validImages.map((image, index) => (
        <img
          key={index}
          src={image}
          alt="Product showcase"
          loading="lazy"
          className={`absolute top-0 left-0 w-full h-full object-contain transition-opacity duration-500 ${
            index === currentIndex ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
    </div>
  );
}

export default function Sunglassess() {
  const [products, setProducts] = useState([]); // State for products
  const [loading, setLoading] = useState(true); // State for loading
  const [error, setError] = useState(null); // State for errors
  const navigate = useNavigate(); // For navigation

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const sunglassessRef = collection(db, "FullFrame"); // Reference to the 'FullFrame' collection
        const querySnapshot = await getDocs(sunglassessRef);
        const fetchedProducts = querySnapshot.docs.map((doc) => ({
          id: doc.id, // Document ID
          ...doc.data(), // Spread the document data
        }));
        setProducts(fetchedProducts); // Update state with fetched products
      } catch (err) {
        console.error("Error fetching products:", err);
        setError(err.message); // Set error message
      } finally {
        setLoading(false); // Set loading to false
      }
    };

    fetchProducts();
  }, []);

  if (loading) {
    return <p className="text-center text-gray-600">Loading products...</p>;
  }

  if (error) {
    return (
      <p className="text-center text-red-500">
        Error loading products: {error}
      </p>
    );
  }

  if (products.length === 0) {
    return (
      <p className="text-center text-gray-500">
        No products found in the "FullFrame" collection.
      </p>
    );
  }

  // Function to handle product click and navigate to ProductDetails page
  const handleProductClick = (product) => {
    navigate("/product-details", { state: product }); // Navigate to ProductDetails with product data
  };

  // Fixed discount rate for every product
  const DISCOUNT_RATE = 10; // 10%

  return (
    <>
    <Header />
      {/* Custom CSS for marquee animation */}
      <style>
        {`
          @keyframes marquee {
            0%   { transform: translateX(100%); }
            100% { transform: translateX(-100%); }
          }
          .animate-marquee {
            animation: marquee 10s linear infinite;
          }
        `}
      </style>
      <div className="container mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl Rubik-Bold text-black px-2">Full Frame</h1>
        </div>
        {/* Product Grid: Three products per row on larger screens */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {products.map((product) => {
            // Ensure product.Cost is a number for calculation
            const newCost = parseFloat(product.Cost) || 0;
            // Old cost is derived by reversing a 10% discount: newCost = oldCost * 0.9 => oldCost = newCost / 0.9
            const oldCost = (newCost / (1 - DISCOUNT_RATE / 100)).toFixed(0);

            return (
              <div
                key={product.id} // Unique key for each product
                className="bg-white rounded-lg shadow-lg transition-transform transform hover:scale-105 cursor-pointer"
                onClick={() => handleProductClick(product)} // Handle click event for product
              >
                {/* Carousel for Product Images */}
                <Carousel
                  images={[
                    product.ImageUrl1,
                    product.ImageUrl2,
                    product.ImageUrl3,
                    product.ImageUrl4,
                  ]}
                />
                {/* Product Details */}
                <div className="px-5 py-4">
                  {/* Price Section */}
                  <div className="Rubik-Bold text-xl text-gray-800 mb-2">
                    <span className="text-black text-2xl mr-2">
                      Price: ₹{newCost.toFixed(0)}
                    </span>
                    {newCost > 0 && (
                      <span className="text-gray-400 line-through mr-2 text-xl">
                        ₹{oldCost}
                      </span>
                    )}
                    {newCost > 0 && (
                      <span className="text-cyan-600 text-xl">
                        ({DISCOUNT_RATE}% OFF)
                      </span>
                    )}
                  </div>
                  {/* Other Product Info */}
                  <div className="flex justify-between mb-1">
                    <p className="Rubik-Medium text-gray-800">
                      {product.Name || "N/A"}
                    </p>
                    <p className="text-gray-500 Rubik-Light">
                      PID: {product.ProductID || "N/A"}
                    </p>
                  </div>
                  <p className="text-black Rubik-Light mb-1">
                    <span className="font-semibold">Frame Color:</span>{" "}
                    <span className="text-gray-600">
                      {product.FrameColor || "Unknown"}
                    </span>
                  </p>
                  <p className="text-black Rubik-Light mb-1">
                    <span className="font-semibold">Material:</span>{" "}
                    <span className="text-gray-600">
                      {product.Material || "Unknown"}
                    </span>
                  </p>
                  <p
                    className={`font-semibold flex items-center ${
                      product.Status === "Available"
                        ? "text-green-500"
                        : product.Status === "Sold"
                        ? "text-red-500"
                        : "text-gray-500"
                    }`}
                  >
                    {product.Status === "Available" && (
                      <span className="inline-flex items-center justify-center rounded-full bg-green-100 p-1 mr-1">
                        <svg
                          className="w-4 h-4 text-green-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </span>
                    )}
                    {product.Status || "Unknown"}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
            <Footer />
      
    </>
  );
}
